import { computed, Injectable, signal } from '@angular/core';
import {
  DEFAULT_FILTER_STATE,
  FilterSpec,
  FilterState,
  SortDirection,
} from '../models/filter.model';
import { todayISO } from '../utils/date-format';

const STORAGE_KEY = 'table_filter_state';
const LOCAL_FILTER_KEYS_SESSION_KEY = 'table_local_filter_keys';
const SELECTION_BASE_RULES_SESSION_KEY = 'table_selection_base_rules';

@Injectable({ providedIn: 'root' })
export class TableStateService {
  // Основной сигнал состояния фильтров
  readonly state = signal<FilterState>(this.loadFromStorage());

  // Удобные computed-сигналы для компонентов
  readonly filters = computed(() => this.state().rules);
  readonly sort = computed(() => this.state().sort);
  readonly page = computed(() => this.state().page);
  readonly pageSize = computed(() => this.state().pageSize);
  readonly visibleColumns = computed(() => this.state().visibleColumns);
  readonly asOfDate = computed(() => this.state().asOfDate);
  // ─── Фильтры ──────────────────────────────────────────────────────────────

  setFilters(rules: FilterSpec[]): void {
    this.update({ rules, page: 1 }); // сброс на первую страницу при смене фильтров
  }

  // ─── Сортировка ────────────────────────────────────────────────────────────

  setSort(field: string, direction: SortDirection): void {
    this.update({ sort: [{ field, direction }] });
  }

  toggleSort(field: string): void {
    const current = this.state().sort.find((s) => s.field === field);

    if (!current) {
      this.setSort(field, 'asc');
    } else if (current.direction === 'asc') {
      this.setSort(field, 'desc');
    } else {
      // Убираем сортировку
      this.update({ sort: this.state().sort.filter((s) => s.field !== field) });
    }
  }

  getSortDirection(field: string): SortDirection | null {
    const dir = this.state().sort.find((s) => s.field === field)?.direction;
    return dir === 'asc' || dir === 'desc' ? dir : null;
  }

  // ─── Пагинация ────────────────────────────────────────────────────────────

  setPage(page: number): void {
    this.update({ page });
  }

  setPageSize(pageSize: number): void {
    this.update({ pageSize, page: 1 });
  }

  // ─── Дата актуальности ─────────────────────────────────────────────────────

  setAsOfDate(asOfDate: string | null): void {
    this.update({ asOfDate, page: 1 });
  }

  // ─── Сброс фильтров и сортировки (при перелогине) ───────────────────────────

  resetFiltersAndSort(): void {
    this.update({ rules: [], sort: [], page: 1, asOfDate: todayISO(), visibleColumns: [] });
  }

  // ─── Видимость колонок ─────────────────────────────────────────────────────

  setVisibleColumns(columns: string[]): void {
    this.update({ visibleColumns: columns });
  }

  // ─── Локальная фильтрация (sessionStorage) ─────────────────────────────────

  saveLocalFilterKeys(keys: Set<string>): void {
    try {
      sessionStorage.setItem(LOCAL_FILTER_KEYS_SESSION_KEY, JSON.stringify([...keys]));
    } catch {}
  }

  loadLocalFilterKeys(): Set<string> | null {
    try {
      const raw = sessionStorage.getItem(LOCAL_FILTER_KEYS_SESSION_KEY);
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch {}
    return null;
  }

  clearLocalFilterKeys(): void {
    try { sessionStorage.removeItem(LOCAL_FILTER_KEYS_SESSION_KEY); } catch {}
  }

  // ─── Сохранённые пользовательские фильтры до filterSelected ────────────────
  // Чтобы при resetLocalFilter (в т.ч. после F5) восстановить sidebar-фильтры,
  // которые были до того, как пользователь нажал «Отфильтровать выбранные».

  saveSelectionBaseRules(rules: FilterSpec[]): void {
    try {
      sessionStorage.setItem(SELECTION_BASE_RULES_SESSION_KEY, JSON.stringify(rules));
    } catch {}
  }

  loadSelectionBaseRules(): FilterSpec[] | null {
    try {
      const raw = sessionStorage.getItem(SELECTION_BASE_RULES_SESSION_KEY);
      if (raw) return JSON.parse(raw) as FilterSpec[];
    } catch {}
    return null;
  }

  clearSelectionBaseRules(): void {
    try { sessionStorage.removeItem(SELECTION_BASE_RULES_SESSION_KEY); } catch {}
  }

  // ─── Персистентность ──────────────────────────────────────────────────────

  private update(partial: Partial<FilterState>): void {
    const next = { ...this.state(), ...partial };
    this.state.set(next);
    this.saveToStorage(next);
  }

  private saveToStorage(state: FilterState): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // sessionStorage может быть недоступен
    }
  }

  private loadFromStorage(): FilterState {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        return { ...DEFAULT_FILTER_STATE, ...(JSON.parse(raw) as Partial<FilterState>) };
      }
    } catch {
      // fallback
    }
    return { ...DEFAULT_FILTER_STATE, asOfDate: todayISO() };
  }
}
